import React, { useMemo, useState } from 'react';
import { Package, Sparkles } from 'lucide-react';
import { TaskyButton, TaskyCard, TaskyInput, TaskyAlert } from '../tasky';
import { ProductSelectionModal, ProductSelectionData } from '../image-generation';
import songAdService, { SongAdProject } from '../../services/api/songAdService';
import SongAdImageKeysField, { SongAdImageKeyItem } from './SongAdImageKeysField';
import { useSetAdmanPageContext } from '../adman/useAdmanPageContext';

/**
 * 歌広告プロジェクトの新規作成パネル（一覧ページ上部の常時表示フォーム）。
 *
 * v5 advisor-notes (3): 作成パネルは**タイトル＋商品選択（任意）＋「作成」だけに縮小**する。
 * CM構成タイプ・音楽ジャンル・曲調・ボーカル・テンポ・尺・比率・イントロ・自由指示・詳細設定は
 * すべて①情報登録タブ（`InfoStep`）に一本化し、ここでは編集しない（backendは title 以外すべて
 * デフォルト値で作成できるため、最小作成でAPI変更は不要）。フル設定を両方に置くと state が二重化し
 * ガードも二重実装になるため、あえて持たせない。
 *
 * 商品選択は既存バナー生成と同じ `ProductSelectionModal` をそのまま使う（新しいUXを発明しない）。
 * 選んだ画像はサムネイル表示し個別に外せる（v5 a）が、ここではアップロードはできない
 * （`SongAdImageKeysField` の `hideUpload`。ローカル画像の追加は①情報登録タブで行う＝v5 b）。
 */

export interface SongAdCreatePanelProps {
  onCreated: (project: SongAdProject) => void;
}

const SongAdCreatePanel: React.FC<SongAdCreatePanelProps> = ({ onCreated }) => {
  const [title, setTitle] = useState('');
  const [selectedProductData, setSelectedProductData] = useState<ProductSelectionData | null>(null);
  // 広告自動化マンに「今この商品を見ている」と伝える（F20-a）。既存ロジックには触れない。
  useSetAdmanPageContext(
    selectedProductData ? { product_ids: [selectedProductData.product.id] } : null
  );
  const [showProductSelectionModal, setShowProductSelectionModal] = useState(false);
  const [imageItems, setImageItems] = useState<SongAdImageKeyItem[]>([]);

  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const productImageKeys = useMemo(() => imageItems.map((i) => i.key), [imageItems]);

  const resetForm = () => {
    setTitle('');
    setSelectedProductData(null);
    setImageItems([]);
    setError(null);
  };

  /**
   * 商品選択モーダルの確定時（v5 a）。選んだ画像をサムネイル表示用の imageItems に反映する
   * （このパネルではアップロードは無いので、常に商品由来の画像で丸ごと置き換えてよい）。
   */
  const handleProductSelected = (data: ProductSelectionData) => {
    setSelectedProductData(data);
    setShowProductSelectionModal(false);
    setImageItems(
      data.selectedImages.map((img) => ({
        key: img.file_path,
        url: img.url,
        label: img.title || img.file_name,
        source: 'product',
      }))
    );
  };

  const handleCreate = async () => {
    if (isCreating) return;
    setIsCreating(true);
    setError(null);
    try {
      const project = await songAdService.createProject({
        title: title.trim() || undefined,
        product_id: selectedProductData?.product.id,
        product_image_keys: productImageKeys.length > 0 ? productImageKeys : undefined,
      });
      resetForm();
      onCreated(project);
    } catch (e) {
      setError(songAdService.extractErrorMessage(e));
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <TaskyCard variant="warm" padding="md" className="mb-6">
      <div className="mb-4">
        <h2 className="text-base font-semibold text-dark">歌広告を作成</h2>
        <p className="text-sm text-gray-500 mt-1">
          曲調・声・テンポは作成後に①情報登録タブで設定できます。
        </p>
      </div>

      <div className="space-y-5">
        {error && <TaskyAlert tone="error">{error}</TaskyAlert>}

        {/* タイトル（任意。空ならサーバー側で自動命名） */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">タイトル（任意）</label>
          <TaskyInput
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreate();
            }}
            maxLength={100}
            placeholder="未入力の場合は自動で名前が付きます"
          />
        </div>

        {/* 商品選択（任意）。選んだ画像はサムネイル表示され product_image_keys に反映される */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">商品選択（任意）</label>
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <TaskyButton type="button" variant="outline" size="sm" onClick={() => setShowProductSelectionModal(true)}>
              <Package className="w-3.5 h-3.5" />
              {selectedProductData ? '商品を変更' : '商品を選択する'}
            </TaskyButton>
            {selectedProductData && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 rounded-full text-xs">
                {selectedProductData.product.name}
              </span>
            )}
          </div>
          {imageItems.length > 0 && (
            <SongAdImageKeysField items={imageItems} onChange={setImageItems} disabled={isCreating} hideUpload />
          )}
        </div>

        <div className="flex justify-end pt-1">
          <TaskyButton type="button" onClick={handleCreate} isLoading={isCreating}>
            <Sparkles className="w-4 h-4" />
            作成する
          </TaskyButton>
        </div>
      </div>

      {showProductSelectionModal && (
        <ProductSelectionModal
          isOpen={showProductSelectionModal}
          onClose={() => setShowProductSelectionModal(false)}
          onConfirm={handleProductSelected}
        />
      )}
    </TaskyCard>
  );
};

export default SongAdCreatePanel;
